import { type TimeSlot } from "@src/appV2/redesign/Shift/types";

/**
 * Internal worker status used by the redesign invite drawer.
 * Normalized from the capitalized `NearbyWorkerStatusLabel` values on the wire
 * ("Favorite" / "Restricted" / "Blocked"). "Favorite" is not surfaced here —
 * starred-ness is tracked separately via `isStarred`.
 */
export const WORKER_STATUS = {
  blocked: "blocked",
  restricted: "restricted",
} as const;

export type WorkerStatus = (typeof WORKER_STATUS)[keyof typeof WORKER_STATUS];

export interface Professional {
  id: string;
  name: string;
  avatarUrl?: string;
  isStarred: boolean;
  /** Number of shifts this professional has worked at this workplace */
  shiftsWorked?: number;
  /** ISO date string of the last shift this professional worked at this workplace */
  lastShiftDate?: string;
  /**
   * Workplace-relative status for this worker. When "blocked", the list item
   * is disabled and cannot be selected. When "restricted", a warning badge
   * renders but the worker can still be invited.
   */
  statusLabel?: WorkerStatus;
}

export interface ShiftDetails {
  startTime: Date;
  endTime: Date;
  timeSlot: TimeSlot;
  qualification: string;
  qualificationDisplayName: string;
  workplaceId: string;
  timezone: string;
  selectedDate: Date;
}

export type InviteFlowStatus =
  | "idle"
  | "blocked"
  | "selecting"
  | "validating"
  | "valid"
  | "warning"
  | "error"
  | "sending"
  | "sent";

export interface InviteBlockReason {
  type: "past-slot" | "start-time-too-soon";
  message: string;
  nextValidSlot?: {
    date: Date;
    slot: TimeSlot;
  };
}

/**
 * Reasons that unconditionally block an invite from being sent.
 * `SHIFT_OVERWORKING` is conditionally an error too — see `InviteErrorReason`
 * and `getInviteValidationError` — but it's not in this list because it can
 * also surface as a warning when the workplace flag is off.
 */
export const INVITE_ERROR_REASONS = [
  "SHIFT_IN_PAST",
  "WORKER_MISSING_LICENSE",
  "WORKER_MISSING_DOCUMENTS",
  "WORKER_DISABLED_TEST_ACCOUNT",
  "WORKER_CANT_CLAIM_SHIFT",
  "WORKER_PAYMENTS_DISABLED",
  "WORKER_PENDING_DOCUMENTS",
  "WORKER_UNSIGNED_AGREEMENT",
  "WORKER_DEACTIVATED",
  "WORKER_BLOCKED",
  "FACILITY_SUSPENDED",
  "WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK",
  "WORKER_HAS_REJECTED_BACKGROUND_CHECK",
  "QUALIFICATION_NOT_ENABLED",
  "SHIFT_DUPLICATE_INVITE",
  "SPENDING_CONTROLS_EXCEEDED",
] as const;

export type InviteErrorReason =
  | (typeof INVITE_ERROR_REASONS)[number]
  | "SHIFT_OVERWORKING"
  | "WORKER_MISSING_REQUIREMENTS";

/**
 * Validate-API rejection reasons that the legacy stepper treats as soft warnings
 * the admin can override. `SHIFT_OVERWORKING` is promoted to an error when the
 * workplace's `restrictOverworkShiftInvites` flag is on.
 */
export const VALIDATE_API_WARNING_REASONS = [
  "SHIFT_OVERWORKING",
  "SHIFT_OVERWORKING_FROM_INVITES",
  "SHIFT_OVERLAPPING",
] as const;

export type ValidateApiWarningReason = (typeof VALIDATE_API_WARNING_REASONS)[number];

/**
 * Warning reasons that can be overridden by the user.
 */
export type InviteWarningReason =
  | { type: "SHIFT_OVERWORKING" }
  | { type: "SHIFT_OVERWORKING_FROM_INVITES" }
  | { type: "SHIFT_OVERLAPPING" }
  | { type: "HIGH_CALLOFF_RATE"; details: { calloffRate: number } }
  | { type: "MISSING_BACKGROUND_CHECK_DATA" }
  | { type: "MISSING_FACILITY_REQUIRED_DOCS"; details: { missingDocuments: string[] } };

export interface ValidationResult {
  isValid: boolean;
  errorReasons: InviteErrorReason[];
  warningReasons: InviteWarningReason[];
  missingDocuments?: string[];
  /** Backend detail message surfaced when no structured `reasons` are returned. */
  detail?: string;
}

export interface InviteValidationParseResult {
  errorReasons: InviteErrorReason[];
  warningReasons: InviteWarningReason[];
  missingDocuments?: string[];
  /** Backend detail message surfaced when no structured `reasons` are returned. */
  detail?: string;
}
